import { Dispatch, SetStateAction, Suspense, useMemo, useState } from "react";
import { Routes, Navigate, Route, useLocation } from "react-router";
import { SkyLoader } from "@skypointcloud/skypoint-ui";
import HeaderSky from "../views/dashboard/header/header";
import SidebarSky from "../views/dashboard/sidebar/sidebar";
import SuccessAndErrorMessage from "./successAndErrorMessage/successAndErrorMessage";
import GlobalSetupBackLink from "./crm/GlobalSetupBackLink";
import { ModeFollowsRoute } from "./packs/ModeFollowsRoute";
import { ScrollToTopOnNavigate } from "./ScrollToTopOnNavigate";
import ErrorBoundary from "./shared/ErrorBoundary";
import RouteErrorBoundary from "./shared/RouteErrorBoundary";
import NotificationProvider from "./Notifications/NotificationProvider";
import { SkyAgentDrawerProvider } from "../views/crm/skyagent/SkyAgentDrawer";
import { NotificationEventProvider } from "./Notifications/NotificationEventContext";
import { NotificationWebSocketConnector } from "./Notifications/NotificationWebSocketConnector";
import { DisplayTzLoader } from "./DisplayTzLoader";
import { TzMismatchBanner } from "./TzMismatchBanner";
import { IRouteDetails, ISessionConfig } from "../common/App.interface";
import { createAppRoutes } from "../routes/appRoutes";
import { RouteGuard } from "./RouteGuard";
import { PackRouteGuard } from "./PackRouteGuard";
import { ProfileRouteGuard } from "./ProfileRouteGuard";
import { HomeLandingGate } from "./HomeLandingGate";
import { ROUTES } from "./shared/constant";
import "../views/crm/crmSkin.css";

interface AppLayoutProps {
  isFullAccess: boolean;
  windowWidth: number | null;
  notificationMessage: string;
  hideNewdashboard: boolean;
  setSessionDefault: Dispatch<SetStateAction<ISessionConfig>>;
}

/**
 * Checks if a route should be rendered based on its accessibility conditions
 */
function shouldRenderRoute(route: IRouteDetails): boolean {
  return (
    !route.accessibilityConditions ||
    (route.accessibilityConditions?.length > 0 &&
      route.accessibilityConditions?.some((condition) => condition === true))
  );
}

/**
 * Main application layout component containing header, sidebar, and content area
 * Pattern: Matches skypoint-skyprivacy's AppLayout architecture
 */
export function AppLayout({
  isFullAccess: _isFullAccess,
  windowWidth,
  notificationMessage,
  hideNewdashboard,
  setSessionDefault,
}: AppLayoutProps) {
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);

  // On CRM routes the shared shell (header + sidebar) should adopt the same
  // Skypoint design-system skin as the content. `display: contents` lets the
  // skin's CSS variables + font inherit into the fixed-positioned header/
  // sidebar without generating a box that would disturb their layout.
  const { pathname } = useLocation();
  const isCrmShell = pathname === "/crm" || pathname.startsWith("/crm/");
  const shellSkin = isCrmShell ? "crm-skin" : undefined;

  // My Profile is a focused settings page with its OWN left section-nav rail, so the
  // global app sidebar is hidden there (and the content margin zeroed) to avoid a
  // two-sidebar look.
  const isProfile =
    pathname === ROUTES.CRM_PROFILE || pathname.startsWith(ROUTES.CRM_PROFILE + "/");

  const routes = useMemo(
    () => createAppRoutes({ hideNewdashboard, setSessionDefault }),
    [hideNewdashboard, setSessionDefault],
  );

  const filteredRoutes = useMemo(
    () => routes.filter((route) => shouldRenderRoute(route)),
    [routes],
  );

  // Note: Subscription check disabled - isInactivePlan check in AppNew.tsx handles deactivation

  return (
    <NotificationEventProvider>
      <DisplayTzLoader />
      <TzMismatchBanner />
      <div className={shellSkin ? `${shellSkin} contents` : "contents"}>
        <HeaderSky />
      </div>
      <ErrorBoundary>
        {!isProfile && (
          <div className={shellSkin ? `${shellSkin} contents` : "contents"}>
            <SidebarSky onCollapseChange={setSidebarCollapsed} />
          </div>
        )}
        <NotificationProvider>
          <NotificationWebSocketConnector />
          {/*
            skyAgent global drawer. Inside NotificationProvider so the agent can
            raise notifications, and wrapping the content so any CRM surface can
            call useSkyAgentDrawer().openSkyAgent(). Staff-facing only.
          */}
          <SkyAgentDrawerProvider>
            <div
              // `100vh - 52px`, not `95vh`. The header above this wrapper is 52px
              // and is in normal flow (it is `sticky`, not `fixed`), so the document
              // is always `52px + wrapper`. With a 95vh minimum that totals
              // `95vh + 52px`, which EXCEEDS the viewport on any screen shorter than
              // ~1040px — every page carried a phantom scrollbar of a few pixels
              // that no page's own content explained. Measured at 588px viewport:
              // doc 610 vs viewport 588, exactly the 22px this arithmetic predicts.
              // 52px is the same constant the full-bleed CRM pages already subtract.
              className={`z-[800] min-h-[calc(100vh-52px)]! bg-white transition-[margin] duration-200 ${
                isProfile ? "md:ml-0!" : sidebarCollapsed ? "md:ml-16!" : "md:ml-[260px]!"
              }`}
            >
              <SuccessAndErrorMessage
                notificationMessage={notificationMessage}
                successClassName="successMessageDashboard"
                failureClassName="errorMessageDashboard"
                warningClassName="warningMessageDashboard"
              />
              <ModeFollowsRoute />
              {/* Every new page starts at the top; the window is the scroll
                  container and nothing else resets it on a route change. */}
              <ScrollToTopOnNavigate />
              <GlobalSetupBackLink />
              <Suspense
                fallback={
                  <div className="flex justify-center items-center min-h-[400px]">
                    <SkyLoader label="Loading..." />
                  </div>
                }
              >
                <RouteGuard>
                  <Routes>
                    {filteredRoutes.map((route) => {
                      const inner = (
                        <RouteErrorBoundary fallbackTitle={`Error loading ${route.name}`}>
                          <route.component
                            realTimeData={route.isRealTimeDataOn ? route.realTimeData : undefined}
                            resetRealTimeData={
                              route.isRealTimeDataOn && route.resetRealTimeData
                                ? route.resetRealTimeData
                                : undefined
                            }
                            windowWidth={windowWidth}
                            {...route.extraProps}
                          />
                        </RouteErrorBoundary>
                      );
                      // /crm and /crm/* are pack-gated (senior_living). RBAC
                      // RouteGuard lets them through (getRequiredNavId → null);
                      // the pack gate is here.
                      //
                      // Day-41+ post-ship: include bare `/crm` (the
                      // CrmLandingRedirect mount point) so it's also wrapped
                      // in PackRouteGuard. Previously the trailing-slash
                      // check missed it — and bare `/crm` was rendered
                      // without pack-gating, which combined with the
                      // RouteGuard fix in routeMapping.ts to surface the
                      // dashboard-toggle bug. Both checks now align on
                      // `=== "/crm" || startsWith("/crm/")`.
                      const isCrmRoute = route.path === "/crm" || route.path.startsWith("/crm/");
                      // The shared self-serve My Profile page is capability-gated,
                      // not operator-gated: ProfileRouteGuard admits on
                      // hasFeature("user_profile") and — unlike PackRouteGuard —
                      // does NOT flip currentMode to "operator_crm" (Workstream B
                      // D-VP-4). ModeFollowsRoute also exempts this path so the
                      // route change itself can't flip the mode either.
                      const isProfileRoute =
                        route.path === ROUTES.CRM_PROFILE ||
                        route.path.startsWith(ROUTES.CRM_PROFILE + "/");
                      // The "/" route lands on the Advisor dashboard; for a
                      // CRM-mode tenant it must instead route into Sky CRM.
                      // HomeLandingGate makes that decision (deferring until
                      // packs resolve), parallel to the /crm PackRouteGuard.
                      const isHomeRoute = route.path === ROUTES.HOME;
                      const element = isProfileRoute ? (
                        <ProfileRouteGuard>
                          {/* Same crm-skin wrapper as PackRouteGuard so the
                              profile looks identical from either entry (D-VP-4). */}
                          <div className="crm-skin">{inner}</div>
                        </ProfileRouteGuard>
                      ) : isCrmRoute ? (
                        <PackRouteGuard>
                          {/* Skypoint design-system skin (Fluent 2 tokens),
                              scoped to all /crm/* routes — see crmSkin.css. */}
                          <div className="crm-skin">{inner}</div>
                        </PackRouteGuard>
                      ) : isHomeRoute ? (
                        <HomeLandingGate>{inner}</HomeLandingGate>
                      ) : (
                        inner
                      );
                      return (
                        <Route
                          key={route.name}
                          path={route.path}
                          element={element}
                        />
                      );
                    })}
                    <Route
                      path="*"
                      element={
                        <Navigate
                          to="/"
                          replace
                        />
                      }
                    />
                  </Routes>
                </RouteGuard>
              </Suspense>
            </div>
          </SkyAgentDrawerProvider>
        </NotificationProvider>
      </ErrorBoundary>
    </NotificationEventProvider>
  );
}

export default AppLayout;
